import {
    type AnyValue,
    type AttributeValueType,
    addSignalAttribute,
    identify,
    init,
    removeSignalAttribute,
    reportError,
    sendEvent,
} from '@dash0/sdk-web';
import { recorder } from '@dash0/sdk-web/session-recording';

type Attributes = Record<string, AttributeValueType | AnyValue>;

let initialized = false;

export const scrubAuthCodeFromUrl = (value: string): string => {
    const url = new URL(value);
    url.searchParams.delete('code');
    const fragment = url.hash.slice(1);
    const queryStart = fragment.indexOf('?');
    const prefix = queryStart === -1 ? '' : fragment.slice(0, queryStart + 1);
    const hash = new URLSearchParams(fragment.slice(queryStart + 1));
    if (hash.has('code')) {
        hash.delete('code');
        url.hash = hash.toString() ? `${prefix}${hash}` : prefix.slice(0, -1);
    }
    return url.toString();
};

export const initializeTelemetry = (
    authToken: string,
    environment: string,
    serviceVersion: string,
    serviceName = 'perci-frontend'
): void => {
    if (initialized) return;

    init({
        serviceName,
        serviceVersion,
        environment,
        endpoint: { url: 'https://ingress.europe-west4.gcp.dash0.com', authToken },
        urlAttributeScrubber: (attributes) => {
            const url = scrubAuthCodeFromUrl(attributes['url.full']);
            return {
                ...attributes,
                'url.full': url,
                'url.query': new URL(url).search.slice(1),
                'url.fragment': new URL(url).hash.slice(1),
            };
        },
        sessionRecording: { recorder, maskAllInputs: false, blockSelector: 'input[type="hidden"]' },
    });
    initialized = true;
};

export const identifyUser = (
    id: string,
    name?: string,
    email?: string,
    claims?: { department?: string; departmentnumber?: string | number; jobtitle?: string }
): void => {
    if (!initialized) return;

    identify(id, { name, email });
    const attributes = {
        'user.department': claims?.department,
        'user.departmentnumber': claims?.departmentnumber,
        'user.jobtitle': claims?.jobtitle,
    };
    for (const [key, value] of Object.entries(attributes)) {
        if (value == null) removeSignalAttribute(key);
        else addSignalAttribute(key, value);
    }
};

export const recordAction = (name: string, attributes?: Attributes): void => {
    sendEvent(name, { attributes });
};

export const recordError = (error: unknown, attributes?: Attributes): void => {
    reportError(error instanceof Error ? error : new Error(String(error)), { attributes });
};
